import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, CheckCircle2, Loader2, MapPin, MessageCircle, Phone, UserRound, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createItineraryRequest } from "@/lib/api";
import { REGIONS } from "@/lib/format";
import { useMediaQuery } from "@/hooks/use-media-query";

const EMPTY_FORM = { guest_name: "", contact_number: "", desired_destination: "", travel_month: "", total_travellers: "" };
const TRAVELLERS = ["1 Traveller", "2 Travellers", "3 Travellers", "4 Travellers", "5 Travellers", "6 Travellers", "7–10 Travellers", "11+ Travellers"];

export const ItineraryForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState("");
  const pending = useRef(false);
  const isPhone = useMediaQuery("(max-width: 639px)");
  const months = useMemo(() => Array.from({ length: 18 }, (_, index) => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + index, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  }), []);

  const update = (key, value) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: undefined }));
    setServerError("");
  };

  async function submit(event) {
    event.preventDefault();
    if (pending.current) return;
    const nextErrors = {};
    const digits = form.contact_number.replace(/\D/g, "");
    if (form.guest_name.trim().length < 2) nextErrors.guest_name = "Please enter your full name.";
    if (!/^[+\d\s()-]+$/.test(form.contact_number) || !/^(?:91)?[6-9]\d{9}$/.test(digits)) nextErrors.contact_number = "Enter a valid 10-digit Indian mobile number.";
    if (!form.desired_destination) nextErrors.desired_destination = "Please choose a destination.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      document.getElementById(`itinerary-${Object.keys(nextErrors)[0]}`)?.focus();
      return;
    }
    pending.current = true;
    setSubmitting(true);
    setServerError("");
    try {
      await createItineraryRequest({ ...form, guest_name: form.guest_name.trim(), contact_number: `+91${digits.slice(-10)}`, travel_month: form.travel_month || "Not decided", total_travellers: form.total_travellers || "Not decided" });
      setSuccess(true);
      toast.success("Your itinerary request has been received.");
    } catch {
      setServerError("We couldn't send your request. Please try again or chat with us on WhatsApp.");
    } finally {
      pending.current = false;
      setSubmitting(false);
    }
  }

  const selectField = (key, label, placeholder, Icon, options, required = false) => (
    <div className={key === "desired_destination" ? "itinerary-destination-field min-w-0 sm:col-span-2" : "itinerary-optional-field min-w-0"}>
      <Label htmlFor={`itinerary-${key}`} className="itinerary-label">{label}</Label>
      <Select value={form[key]} onValueChange={(value) => update(key, value)} disabled={submitting}>
        <SelectTrigger id={`itinerary-${key}`} data-testid={`itinerary-${key}-select`} aria-required={required} aria-invalid={!!errors[key]} aria-describedby={errors[key] ? `itinerary-${key}-error` : undefined} className="itinerary-control">
          <div className="itinerary-select-value flex min-w-0 items-center gap-2.5"><Icon aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground/60" /><SelectValue placeholder={isPhone && key === "total_travellers" ? "Travellers" : placeholder} /></div>
        </SelectTrigger>
        <SelectContent className="itinerary-options" data-testid={`itinerary-${key}-options`}>
          {options.map((value, index) => <SelectItem key={value} value={value} data-testid={`itinerary-${key}-option-${index}`} className="min-h-11">{value}</SelectItem>)}
        </SelectContent>
      </Select>
      {errors[key] && <p id={`itinerary-${key}-error`} data-testid={`itinerary-${key}-error`} className="itinerary-error">{errors[key]}</p>}
    </div>
  );

  return (
    <Card id="plan-my-trip" data-testid="itinerary-form-card" className="itinerary-card">
      {success ? <div data-testid="itinerary-form-success" role="status" className="flex min-h-80 flex-col items-start justify-center gap-5">
        <CheckCircle2 className="h-12 w-12 text-primary" aria-hidden="true" />
        <h2 className="text-2xl font-bold">Your next escape is taking shape.</h2>
        <p className="leading-relaxed text-muted-foreground">Thanks, {form.guest_name.trim()}! We've received your request for {form.desired_destination}. A travel expert will contact you shortly on {form.contact_number}.</p>
        <Button className="landing-primary-button" data-testid="itinerary-request-another-button" onClick={() => { setSuccess(false); setForm(EMPTY_FORM); }}>Plan another trip <ArrowRight /></Button>
      </div> : <>
        <h2 data-testid="itinerary-form-title" className="itinerary-title">Get your custom itinerary</h2>
        <form onSubmit={submit} noValidate data-testid="itinerary-form">
          <div className="itinerary-fields mt-5 grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
            <div className="min-w-0">
              <Label htmlFor="itinerary-guest_name" className="itinerary-label">Full name</Label>
              <div className="relative"><UserRound className="itinerary-input-icon" aria-hidden="true" />
                <Input id="itinerary-guest_name" data-testid="itinerary-full-name-input" autoComplete="name" required maxLength={100} placeholder="Enter your full name" value={form.guest_name} onChange={(e) => update("guest_name", e.target.value)} disabled={submitting} aria-invalid={!!errors.guest_name} aria-describedby={errors.guest_name ? "itinerary-guest-name-error" : undefined} className="itinerary-control pl-10" />
              </div>
              {errors.guest_name && <p id="itinerary-guest-name-error" data-testid="itinerary-name-error" className="itinerary-error">{errors.guest_name}</p>}
            </div>
            <div className="min-w-0">
              <Label htmlFor="itinerary-contact_number" className="itinerary-label">Mobile number</Label>
              <div className="relative"><Phone className="itinerary-input-icon" aria-hidden="true" />
                <Input id="itinerary-contact_number" data-testid="itinerary-mobile-input" autoComplete="tel" type="tel" inputMode="tel" required maxLength={20} placeholder="+91 98765 43210" value={form.contact_number} onChange={(e) => update("contact_number", e.target.value)} disabled={submitting} aria-invalid={!!errors.contact_number} aria-describedby={errors.contact_number ? "itinerary-mobile-error" : undefined} className="itinerary-control pl-10" />
              </div>
              {errors.contact_number && <p id="itinerary-mobile-error" data-testid="itinerary-mobile-error" className="itinerary-error">{errors.contact_number}</p>}
            </div>
            {selectField("desired_destination", "Choose destination", "Select destination", MapPin, REGIONS, true)}
            {selectField("travel_month", "Travel month", "Select month", CalendarDays, [...months, "Not decided"])}
            {selectField("total_travellers", "Travellers", "Select travellers", UsersRound, TRAVELLERS)}
          </div>
          {serverError && <p data-testid="itinerary-server-error" role="alert" className="itinerary-error mt-4">{serverError}</p>}
          <Button type="submit" disabled={submitting} data-testid="itinerary-form-submit-button" className="landing-primary-button mt-5 w-full">
            {submitting ? <><Loader2 className="animate-spin" /> Sending your request…</> : <>Get My Custom Itinerary <ArrowRight /></>}
          </Button>
          <p data-testid="itinerary-form-note" className="mt-4 text-center text-sm leading-relaxed text-muted-foreground">No obligation to book. A travel expert will contact you shortly.</p>
        </form>
      </>}
      <Button asChild variant="outline" className="itinerary-whatsapp-button mt-6 w-full" data-testid="itinerary-form-whatsapp-button">
        <a href="https://wa.me/919816672006?text=Hi%20Best%20Escape%20Holidays!%20I'd%20like%20to%20plan%20a%20custom%20trip." target="_blank" rel="noopener noreferrer"><MessageCircle /> Chat on WhatsApp</a>
      </Button>
      <p className="mt-3 text-center text-xs leading-relaxed text-muted-foreground" data-testid="itinerary-legal-note">By submitting, you agree to our <Link to="/terms-and-conditions" data-testid="itinerary-terms-link" className="legal-inline-link">Terms</Link> and <Link to="/privacy-policy" data-testid="itinerary-privacy-link" className="legal-inline-link">Privacy Policy</Link>.</p>
    </Card>
  );
};
